import { useState, type FormEvent } from 'react'
import { GERMAN_STATES, GERMAN_STATE_LABELS, parsePriceCents, type FederalState, type PatchSettingsRequest, type SettingsResponse } from '@wf/shared'
import { minToTime } from '../orders/schedule'
import { parseTimeToMin } from '../planung/planung'
import { usePatchSettings } from './useSettings'

function Field({
  label,
  value,
  onChange,
  placeholder,
  type,
  inputMode,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: 'email' | 'time'
  inputMode?: 'numeric' | 'decimal' | 'tel' | 'email'
}) {
  return (
    <label className="wf-field">
      <span className="wf-label">{label}</span>
      <input className="wf-input" value={value} placeholder={placeholder} type={type} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

/**
 * Einstellungen: Werkstatt-Grunddaten + §14-Firmendaten für die Rechnung. `billing` ist null, wenn der
 * Nutzer keine Berechtigung für Firmen-/Bankdaten hat — dann erscheint nur der Werkstatt-Block. Geld
 * (Stundensatz) über @wf/shared (parsePriceCents). Eigene Komponente, mit `initial` aus GET /settings.
 */
export function SettingsForm({ initial }: { initial: SettingsResponse }) {
  const patch = usePatchSettings()
  const billing = initial.billing
  const [formError, setFormError] = useState<string | null>(null)
  const [f, setF] = useState(() => ({
    name: billing?.name ?? '',
    bays: String(initial.bays),
    // Öffnungszeit-Fenster (Plantafel-Zeitachse + Auslastungs-Kapazität) als <input type=time>-Werte.
    openStart: minToTime(initial.openStartMin ?? 480),
    openEnd: minToTime(initial.openEndMin ?? 1020),
    autoLogoutMin: String(initial.autoLogoutMin),
    federalState: initial.federalState ?? '',
    ausgleichszeitraum: initial.ausgleichszeitraum ?? '',
    beraternummer: initial.beraternummer ?? '',
    mandantennummer: initial.mandantennummer ?? '',
    holidayCatholicMunicipality: Boolean(initial.holidayCatholicMunicipality),
    laborRate: ((initial.laborRateCents ?? 0) / 100).toFixed(2).replace('.', ','),
    legalName: billing?.legalName ?? '',
    ceo: billing?.ceo ?? '',
    address: billing?.address ?? '',
    zip: billing?.zip ?? '',
    city: billing?.city ?? '',
    taxNumber: billing?.taxNumber ?? '',
    vatId: billing?.vatId ?? '',
    kleinunternehmer: Boolean(billing && billing.kleinunternehmer),
    iban: billing?.iban ?? '',
    bank: billing?.bank ?? '',
    invPhone: billing?.invPhone ?? '',
    invEmail: billing?.invEmail ?? '',
  }))
  const set = (key: keyof typeof f) => (value: string) => setF((prev) => ({ ...prev, [key]: value }))

  function submit(event: FormEvent) {
    event.preventDefault()
    const body: PatchSettingsRequest = {
      name: f.name,
      bays: Number.parseInt(f.bays, 10) || 0,
      autoLogoutMin: Number.parseInt(f.autoLogoutMin, 10) || 0,
      federalState: f.federalState,
      ausgleichszeitraum: f.ausgleichszeitraum,
      beraternummer: f.beraternummer,
      mandantennummer: f.mandantennummer,
      holidayCatholicMunicipality: f.holidayCatholicMunicipality,
      laborRateCents: parsePriceCents(f.laborRate),
    }
    // Öffnungszeiten: leeres/ungültiges Feld blockiert den Submit EHRLICH (kein stilles Weglassen mit
    // „Gespeichert ✓", Review-Fund 15.07.); die Paar-Gültigkeit (Ende > Start) prüft der Server (400).
    setFormError(null)
    const openStartMin = parseTimeToMin(f.openStart)
    const openEndMin = parseTimeToMin(f.openEnd)
    if (openStartMin == null || openEndMin == null) {
      setFormError('Öffnungszeiten unvollständig — bitte beide Uhrzeiten (HH:MM) angeben.')
      return
    }
    body.openStartMin = openStartMin
    body.openEndMin = openEndMin
    if (billing) {
      Object.assign(body, {
        legalName: f.legalName,
        ceo: f.ceo,
        address: f.address,
        zip: f.zip,
        city: f.city,
        taxNumber: f.taxNumber,
        vatId: f.vatId,
        kleinunternehmer: f.kleinunternehmer,
        iban: f.iban,
        bank: f.bank,
        invPhone: f.invPhone,
        invEmail: f.invEmail,
      } satisfies Omit<PatchSettingsRequest, 'name' | 'bays' | 'autoLogoutMin' | 'laborRateCents'>)
    }
    patch.mutate(body)
  }

  const errorMsg = patch.error instanceof Error ? patch.error.message : null

  return (
    <form onSubmit={submit} className="wf-settings">
      <div className="wf-sectionhead">
        <h3>Werkstatt</h3>
      </div>
      <Field label="Name der Werkstatt" value={f.name} onChange={set('name')} placeholder="Auto Müller" />
      <div className="two">
        <Field label="Arbeitsplätze" value={f.bays} onChange={set('bays')} inputMode="numeric" />
        <Field label="Auto-Abmeldung (Min, 0 = aus)" value={f.autoLogoutMin} onChange={set('autoLogoutMin')} inputMode="numeric" />
      </div>
      <div className="two">
        <Field label="Öffnung von (Plantafel)" type="time" value={f.openStart} onChange={set('openStart')} />
        <Field label="Öffnung bis" type="time" value={f.openEnd} onChange={set('openEnd')} />
      </div>
      <label className="wf-field">
        <span className="wf-label">Bundesland (für Feiertage im Zeit-Soll)</span>
        <select className="wf-input" value={f.federalState} onChange={(e) => set('federalState')(e.target.value)}>
          <option value="">— nicht gesetzt (keine Feiertags-Berücksichtigung) —</option>
          {GERMAN_STATES.map((s) => (
            <option key={s} value={s}>
              {GERMAN_STATE_LABELS[s as FederalState]}
            </option>
          ))}
        </select>
      </label>
      {/* § 3 Satz 2 ArbZG stellt beide Ausgleichszeiträume zur Wahl; § 7 erlaubt per Tarif-/Betriebs-
          vereinbarung abweichende. Ohne Festlegung rechnet der Durchschnittswächter BEIDE und weist einen
          uneindeutigen Stand ehrlich als solchen aus, statt sich eine Variante auszusuchen. */}
      <label className="wf-field">
        <span className="wf-label">Ausgleichszeitraum (§ 3 ArbZG, Acht-Stunden-Durchschnitt)</span>
        <select className="wf-input" value={f.ausgleichszeitraum} onChange={(e) => set('ausgleichszeitraum')(e.target.value)}>
          <option value="">— nicht festgelegt (beide gesetzlichen Varianten prüfen) —</option>
          <option value="24_wochen">24 Wochen</option>
          <option value="6_monate">Sechs Kalendermonate</option>
        </select>
      </label>
      <div className="two">
        <Field label="DATEV-Beraternummer (4–7 Ziffern, optional)" value={f.beraternummer} onChange={set('beraternummer')} />
        <Field label="DATEV-Mandantennummer (1–5 Ziffern, optional)" value={f.mandantennummer} onChange={set('mandantennummer')} />
      </div>
      {(f.federalState === 'BY' || f.federalState === 'SN' || f.federalState === 'TH') && (
        <label className="wf-check">
          <input
            type="checkbox"
            checked={f.holidayCatholicMunicipality}
            onChange={(e) => setF((prev) => ({ ...prev, holidayCatholicMunicipality: e.target.checked }))}
          />
          <span>Überwiegend katholische Gemeinde (zählt {f.federalState === 'BY' ? 'Mariä Himmelfahrt' : 'Fronleichnam'} als Feiertag)</span>
        </label>
      )}
      <Field label="Stundensatz (€)" value={f.laborRate} onChange={set('laborRate')} inputMode="decimal" />

      {billing ? (
        <>
          <div className="wf-sectionhead">
            <h3>Firmendaten (§14 Rechnung)</h3>
          </div>
          <Field label="Firmenname (Rechnungsabsender)" value={f.legalName} onChange={set('legalName')} placeholder="Auto Müller GmbH" />
          <Field label="Inhaber/in · Geschäftsführer/in" value={f.ceo} onChange={set('ceo')} placeholder="Hans Müller" />
          <Field label="Straße & Hausnummer" value={f.address} onChange={set('address')} placeholder="Musterstraße 12" />
          <div className="two">
            <Field label="PLZ" value={f.zip} onChange={set('zip')} inputMode="numeric" placeholder="12345" />
            <Field label="Ort" value={f.city} onChange={set('city')} placeholder="Musterstadt" />
          </div>
          <div className="two">
            <Field label="Steuernummer" value={f.taxNumber} onChange={set('taxNumber')} placeholder="12/345/67890" />
            <Field label="USt-IdNr. (falls vorhanden)" value={f.vatId} onChange={set('vatId')} placeholder="DE123456789" />
          </div>
          <label className="wf-checkbox">
            <input
              type="checkbox"
              checked={f.kleinunternehmer}
              onChange={(e) => setF((prev) => ({ ...prev, kleinunternehmer: e.target.checked }))}
            />
            Ich bin Kleinunternehmer (§19 UStG) – keine Umsatzsteuer ausweisen
          </label>

          <div className="wf-sectionhead">
            <h3>Bank &amp; Rechnung</h3>
          </div>
          <div className="two">
            <Field label="IBAN (für die Fußzeile)" value={f.iban} onChange={set('iban')} placeholder="DE00 0000 0000 0000 0000 00" />
            <Field label="Bank" value={f.bank} onChange={set('bank')} placeholder="Sparkasse Musterstadt" />
          </div>
          <div className="two">
            <Field label="Telefon (Rechnung)" value={f.invPhone} onChange={set('invPhone')} inputMode="tel" placeholder="0123 456789" />
            <Field label="E-Mail (Rechnung)" value={f.invEmail} onChange={set('invEmail')} type="email" inputMode="email" placeholder="info@werkstatt.de" />
          </div>
        </>
      ) : null}

      {formError ? (
        <p className="error" role="alert">
          {formError}
        </p>
      ) : null}
      {errorMsg ? (
        <p className="error" role="alert">
          {errorMsg}
        </p>
      ) : null}
      {patch.isSuccess ? (
        <p className="hint" role="status">
          Gespeichert ✓
        </p>
      ) : null}
      <div className="wf-listfoot">
        <button type="submit" className="wf-btn" disabled={patch.isPending}>
          {patch.isPending ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </form>
  )
}
